/**
 * File-related utility functions
 * Helper functions for file name extraction, type detection, and formatting
 */

/**
 * Extract filename from full file URL path
 * @param {string} fileUrl - Full blob URL
 * @returns {string} - Extracted filename
 *
 * Example:
 * Input: "https://genserve.blob.core.windows.net/rag-files/20250808/f85c7d6b-7a64-4b13-b8b7-5a458b0ff7ab/f82effeb-4adb-4fad-a1f5-6737d4497421_HIPAA-1.pdf"
 * Output: "HIPAA-1.pdf"
 */
export const extractFileName = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") {
    return "Unknown File";
  }

  try {
    // Get the last part of the URL path
    const pathParts = fileUrl.split("/");
    const fullFileName = pathParts[pathParts.length - 1];

    // Extract everything after the last underscore
    const underscoreIndex = fullFileName.lastIndexOf("_");
    if (underscoreIndex !== -1 && underscoreIndex < fullFileName.length - 1) {
      return fullFileName.substring(underscoreIndex + 1);
    }

    // Fallback: return the full filename if no underscore found
    return fullFileName;
  } catch (error) {
    console.error("Error extracting filename:", error);
    return "Unknown File";
  }
};

/**
 * Extract file type from file URL extension
 * @param {string} fileUrl - Full file URL
 * @returns {string} - File type in uppercase
 *
 * Example:
 * Input: "...file.pdf" → Output: "PDF"
 * Input: "...file.csv" → Output: "CSV"
 */
export const extractFileType = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") {
    return "UNKNOWN";
  }

  try {
    const fileName = extractFileName(fileUrl);
    const dotIndex = fileName.lastIndexOf(".");

    if (dotIndex !== -1 && dotIndex < fileName.length - 1) {
      return fileName.substring(dotIndex + 1).toUpperCase();
    }

    return "UNKNOWN";
  } catch (error) {
    console.error("Error extracting file type:", error);
    return "UNKNOWN";
  }
};

/**
 * Format current date for display
 * @returns {string} - Formatted date string (MM/DD/YYYY)
 */
export const formatCurrentDate = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const year = now.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "";

  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return Math.round((bytes / Math.pow(1024, i)) * 10) / 10 + " " + sizes[i];
};

/**
 * Clean filename by removing UUID prefix
 * @param {string} filename - Full filename with UUID prefix
 * @returns {string} - Clean filename
 *
 * Example:
 * Input: "f82effeb-4adb-4fad-a1f5-6737d4497421_HIPAA-1.pdf"
 * Output: "HIPAA-1.pdf"
 */
export const cleanFileName = (filename) => {
  if (!filename || typeof filename !== "string") {
    return "Unknown File";
  }

  try {
    // Find the first underscore and extract everything after it
    const underscoreIndex = filename.indexOf("_");
    if (underscoreIndex !== -1 && underscoreIndex < filename.length - 1) {
      return filename.substring(underscoreIndex + 1);
    }

    // Fallback: return the full filename if no underscore found
    return filename;
  } catch (error) {
    console.error("Error cleaning filename:", error);
    return "Unknown File";
  }
};

/**
 * Format ISO date string to readable format
 * @param {string} isoDateString - ISO date string
 * @returns {string} - Formatted date
 *
 * Example:
 * Input: "2025-08-07T22:30:54.673991"
 * Output: "Aug 7, 2025"
 */
export const formatDate = (isoDateString) => {
  if (!isoDateString) {
    return formatCurrentDate();
  }

  try {
    const date = new Date(isoDateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return formatCurrentDate();
  }
};
